"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.notifyUserChanged = exports.clearCachedUser = exports.cacheUser = exports.getCachedUser = exports.userHasChanged = exports.userDataChanged = exports.permissionsChanged = void 0;
const core_utility_1 = require("@built/core-utility");
const events_1 = require("./events");
const token_1 = require("./authentication/token");
const PERM_CHANGE_REASON = 'permissions-change';
/** Checks to see if permissions are different between the given users. */
const permissionsChanged = (current, previous) => {
    // we are going to assume that the most common case is equality, so we're not doing some short-circuit checks
    // and are optimizing for always having to check all permissions using maps/sets
    const currentGroups = new Map(current.permissionGroups.map((g) => [g.id, g.permissions]));
    const previousGroups = new Map(previous.permissionGroups.map((g) => [g.id, g.permissions]));
    if (currentGroups.size !== previousGroups.size) {
        return PERM_CHANGE_REASON;
    }
    for (const [id, perms] of currentGroups) {
        const currentPerms = new Set(perms);
        const prevPerms = new Set(previousGroups.get(id));
        if (currentPerms.size !== prevPerms.size) {
            return PERM_CHANGE_REASON;
        }
        for (const perm of currentPerms) {
            if (!prevPerms.has(perm)) {
                return PERM_CHANGE_REASON;
            }
        }
    }
    return false;
};
exports.permissionsChanged = permissionsChanged;
/** Checks if user profile data (name, email, etc.) are different between the given users. */
const userDataChanged = (current, previous) => {
    // extract the more specific things we otherwise check for explicitly, compare the rest
    const { userUid, tenant, token, permissionGroups } = current, userData = __rest(current, ["userUid", "tenant", "token", "permissionGroups"]);
    const { userUid: prevUserUid, tenant: prevTenant, token: prevToken, permissionGroups: prevPermGroups } = previous, prevUserData = __rest(previous, ["userUid", "tenant", "token", "permissionGroups"]);
    if (!(0, core_utility_1.isDeepEqual)(userData, prevUserData)) {
        return 'edit';
    }
    return false;
};
exports.userDataChanged = userDataChanged;
const userChangeChecks = [
    (current, previous) => (current.userUid !== previous.userUid ? 'identity-change' : false),
    (current, previous) => current.tenant.tenantUid !== previous.tenant.tenantUid || current.tenant.name !== previous.tenant.name
        ? 'tenant-change'
        : false,
    (current, previous) => (current.token.rawToken !== previous.token.rawToken ? 'token-change' : false),
    exports.permissionsChanged,
    exports.userDataChanged,
];
/**
 * Gets if the user has changed and why.
 *
 * Returns false if current user is the same as previous.
 *
 * @param current - current/new user
 * @param previous - user to compare new user against; if not provided, assumed to be a load
 */
const userHasChanged = (current, previous) => {
    if (!previous) {
        // no prior user data, so we assume it's a load
        return 'load';
    }
    for (const check of userChangeChecks) {
        const reason = check(current, previous);
        if (reason) {
            return reason;
        }
    }
    return false;
};
exports.userHasChanged = userHasChanged;
const DEFAULT_CACHE = {};
// we use local storage so it will be available across tabs; if in future we want to support different users on different tabs, could swap to session
const userCache = new core_utility_1.InBrowserStorage('Built.User', DEFAULT_CACHE, { provider: 'localStorage' });
/**
 * Gets user cached in local storage, if exists and the auth token is still the same as current user token.
 */
const getCachedUser = async () => {
    const cache = userCache.get();
    if (cache.user) {
        // get current token and ensure it's the same as the cached user's token
        const currentToken = await (0, token_1.getMonitoredTokenData)();
        if (currentToken.ok && currentToken.data) {
            if (cache.user.token.rawToken === currentToken.data.rawToken) {
                return cache.user;
            }
        }
        (0, exports.clearCachedUser)();
    }
    return undefined;
};
exports.getCachedUser = getCachedUser;
/** Saves the given user in the user cache. */
const cacheUser = (user) => {
    userCache.save({ user });
};
exports.cacheUser = cacheUser;
/** Clears user cache. */
const clearCachedUser = () => {
    userCache.save(DEFAULT_CACHE);
};
exports.clearCachedUser = clearCachedUser;
/**
 * Checks if the current user has changed from the previous user and notifies listeners if so.
 *
 * @param current - current/new user
 * @param previous - user to compare new user against; if not provided, assumed to be new user and will notify
 *
 * @returns the reason the user has changed, if it has; false otherwise
 */
const notifyUserChanged = (current, previous) => {
    const reason = (0, exports.userHasChanged)(current, previous);
    if (reason) {
        events_1.authUserProfileRefreshed.notify({
            reason,
            user: current,
        });
    }
    return reason;
};
exports.notifyUserChanged = notifyUserChanged;
